import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckboxCard, CheckboxCardSkeleton } from "@/components/CheckboxCard";
import { FormActions } from "@jbnl/shared-ui";
import { SelectAllCheckbox } from "@/components/SelectAllCheckbox";
import { Button } from "@/components/ui/button";
import { FieldLegend, FieldSet } from "@/components/ui/field";
import {
  Skeleton,
  SkeletonLine,
  SkeletonRegion,
} from "@/components/ui/skeleton";
import { datumLang } from "@/i18n/datum";
import { ReistijdSortering } from "@/components/ReistijdSortering";
import {
  gesorteerdOpReistijd,
  reistijdLabel,
  type Reistijdsortering,
} from "@/lib/reistijd";
import {
  useBeschikbaarheid,
  useSetBeschikbaarheid,
} from "./useBeschikbaarheid";

interface Props {
  inschrijfperiodeId: string | undefined;
  persoonId: string | undefined;
  /**
   * Whether the window is shut. The list is still drawn - no read is ever gated, and an
   * ondersteuner has to be able to check what they answered - but nothing on it can move.
   */
  gesloten?: boolean;
}

type Antwoord = NonNullable<ReturnType<typeof useBeschikbaarheid>["data"]>;
type Dag = Antwoord["dagen"][number];

const toggle = (id: string) => (huidig: string[]) =>
  huidig.includes(id) ? huidig.filter((x) => x !== id) : [...huidig, id];

/** The ids already ticked in the server's answer. */
const gekozenUit = (data: Antwoord | undefined) => ({
  dagen:
    data?.dagen.filter((d) => d.beschikbaar).map((d) => d.wedstrijddagId) ?? [],
  klassen: data?.klassen.filter((k) => k.gewenst).map((k) => k.klasseId) ?? [],
});

/**
 * Saved as a whole set: geen beschikbaarheid is the absence of a row, so an unticked box is the
 * only way to say "not this one". The form asks for days and an optional klasse wish and never
 * for a functie - the planning draws that from the bevoegdheden (ADR-0077).
 */
export function BeschikbaarheidForm({
  inschrijfperiodeId,
  persoonId,
  gesloten,
}: Props) {
  const { t } = useTranslation(["beschikbaarheid", "common"]);
  const { data, isPending, isError } = useBeschikbaarheid(
    inschrijfperiodeId,
    persoonId,
  );
  const opslaan = useSetBeschikbaarheid(inschrijfperiodeId, persoonId);

  const [gekozenDagen, setGekozenDagen] = useState<string[]>([]);
  const [gekozenKlassen, setGekozenKlassen] = useState<string[]>([]);
  const [geladen, setGeladen] = useState(data);

  // Re-seeded during render whenever the server's answer changes - including after a save, and
  // after the beheerder picks a different persoon. Not an effect: an effect would render the
  // stale ticks once before correcting them.
  if (data !== geladen) {
    const uit = gekozenUit(data);
    setGeladen(data);
    setGekozenDagen(uit.dagen);
    setGekozenKlassen(uit.klassen);
  }

  if (!inschrijfperiodeId || !persoonId) {
    return null;
  }

  if (isPending) {
    return <BeschikbaarheidFormSkeleton />;
  }

  if (isError) {
    return <p className="text-sm text-destructive">{t("errors.ophalen")}</p>;
  }

  if (data.dagen.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("geenDagen")}</p>;
  }

  const actions = <Acties opslaan={opslaan} gesloten={gesloten} />;

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();

        opslaan.mutate({
          wedstrijddagIds: gekozenDagen,
          klasseIds: gekozenKlassen,
        });
      }}
    >
      {/* Below the empty states, because it is only true once there is something to tick - and
        in the description it would vanish exactly when someone comes back to check. */}
      <p className="text-sm text-muted-foreground">{t("uitleg")}</p>
      <p className="text-sm text-muted-foreground">{t("reistijdUitleg")}</p>

      {actions}

      <DagenKeuze
        dagen={data.dagen}
        gekozen={gekozenDagen}
        onChange={setGekozenDagen}
        gesloten={gesloten}
      />

      {data.klassen.length > 0 && (
        <KlassenKeuze
          klassen={data.klassen}
          gekozen={gekozenKlassen}
          onChange={setGekozenKlassen}
          gesloten={gesloten}
        />
      )}

      {actions}
    </form>
  );
}

/** Drawn at both ends since #117: the outcome travels with the button that was pressed. */
function Acties({
  opslaan,
  gesloten,
}: {
  opslaan: ReturnType<typeof useSetBeschikbaarheid>;
  gesloten?: boolean;
}) {
  const { t } = useTranslation(["beschikbaarheid", "common"]);

  return (
    <FormActions>
      {opslaan.isSuccess && !opslaan.isPending && (
        <p className="text-sm text-muted-foreground">{t("opgeslagen")}</p>
      )}

      {opslaan.isError && (
        <p className="text-sm text-destructive">{opslaan.error.message}</p>
      )}

      {/*
        Disabled rather than hidden: the button going missing reads as a screen that failed
        to load, and the card above already says why nothing can move.
      */}
      <Button type="submit" disabled={opslaan.isPending || gesloten}>
        {opslaan.isPending ? t("common:actions.busy") : t("opslaan")}
      </Button>
    </FormActions>
  );
}

function DagenKeuze({
  dagen,
  gekozen,
  onChange,
  gesloten,
}: {
  dagen: Dag[];
  gekozen: string[];
  onChange: (volgende: (huidig: string[]) => string[]) => void;
  gesloten?: boolean;
}) {
  const { t } = useTranslation("beschikbaarheid");

  // The same control a korps gets on the inschrijving: the dag they can actually reach is often
  // the question an ondersteuner is answering (ADR-0053).
  const [sortering, setSortering] = useState<Reistijdsortering>("datum");

  return (
    <FieldSet>
      <FieldLegend variant="label">{t("legend")}</FieldLegend>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <SelectAllCheckbox
          id="beschikbaarheid-alles"
          total={dagen.length}
          selected={gekozen.length}
          disabled={gesloten}
          onChange={(alles) =>
            onChange(() =>
              alles ? dagen.map((dag) => dag.wedstrijddagId) : [],
            )
          }
        />

        {/* Never disabled by `gesloten`: reading how the list is ordered is not a change. */}
        <ReistijdSortering waarde={sortering} onKies={setSortering} />
      </div>

      <ul className="grid gap-2 sm:grid-cols-2">
        {gesorteerdOpReistijd(dagen, sortering).map((dag) => (
          <li key={dag.wedstrijddagId}>
            <CheckboxCard
              id={`dag-${dag.wedstrijddagId}`}
              title={datumLang(dag.date)}
              description={<DagRegel dag={dag} />}
              checked={gekozen.includes(dag.wedstrijddagId)}
              disabled={gesloten}
              onCheckedChange={() => onChange(toggle(dag.wedstrijddagId))}
            />
          </li>
        ))}
      </ul>
    </FieldSet>
  );
}

/** Unknown is spelled out: a dash beside a date reads as "no distance at all" (ADR-0053). */
function DagRegel({ dag }: { dag: Dag }) {
  const { t } = useTranslation("beschikbaarheid");

  return (
    <>
      {t("dagRegel", {
        niveau: dag.niveauName,
        korps: dag.organiserendKorpsName,
      })}
      {" · "}
      {dag.reistijdSeconds == null
        ? t("reistijdOnbekend")
        : t("reistijdRegel", {
            reistijd: reistijdLabel(t, dag.reistijdSeconds),
          })}
    </>
  );
}

function KlassenKeuze({
  klassen,
  gekozen,
  onChange,
  gesloten,
}: {
  klassen: Antwoord["klassen"];
  gekozen: string[];
  onChange: (volgende: (huidig: string[]) => string[]) => void;
  gesloten?: boolean;
}) {
  const { t } = useTranslation("beschikbaarheid");

  return (
    <FieldSet>
      <FieldLegend variant="label">{t("klassen.legend")}</FieldLegend>
      <p className="text-sm text-muted-foreground">{t("klassen.uitleg")}</p>

      <ul className="grid gap-2 sm:grid-cols-2">
        {klassen.map((klasse) => (
          <li key={klasse.klasseId}>
            <CheckboxCard
              id={`klasse-${klasse.klasseId}`}
              title={klasse.klasseName}
              checked={gekozen.includes(klasse.klasseId)}
              disabled={gesloten}
              onCheckedChange={() => onChange(toggle(klasse.klasseId))}
            />
          </li>
        ))}
      </ul>
    </FieldSet>
  );
}

/** Both rows, or the list drops down the page when the answer lands. */
function BeschikbaarheidFormSkeleton() {
  const { t } = useTranslation(["beschikbaarheid", "common"]);

  const skeletonActions = (
    <FormActions>
      <Skeleton className="h-9 w-48" />
    </FormActions>
  );

  return (
    <SkeletonRegion className="space-y-4">
      {/* The explanation is grey rather than drawn for real: it is only true once there is
          something to tick, and whether there is is what the server has not answered yet. */}
      <div>
        <SkeletonLine className="w-full" />
        <SkeletonLine className="w-full" />
        <SkeletonLine className="w-3/4" />
      </div>

      {skeletonActions}

      <FieldSet>
        <FieldLegend variant="label">{t("legend")}</FieldLegend>

        {/* The tri-state box and the sort toggle share a row, at the height it will have. */}
        <div className="flex h-9 items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Skeleton className="size-4 rounded-sm" />
            <Skeleton className="h-3.5 w-44" />
          </div>
          <Skeleton className="h-8 w-48" />
        </div>

        <ul className="grid gap-2 sm:grid-cols-2">
          {Array.from({ length: 6 }, (_, dag) => (
            <li key={dag}>
              <CheckboxCardSkeleton />
            </li>
          ))}
        </ul>
      </FieldSet>

      {skeletonActions}
    </SkeletonRegion>
  );
}
